import {
  activityLogsApiV1InternalActivityLogsGet,
  dashboardApiV1InternalDashboardGet,
  healthApiV1InternalHealthDashboardGet,
  metricsApiV1InternalMetricsDashboardGet,
  pilotControlsApiV1InternalPilotControlsGet,
} from '@closet-theory/api';
import type {
  AdminPilotControls,
  ActivityLogItem,
  DashboardSummary,
  HealthSummary,
  MetricsSummary,
} from '@closet-theory/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Section, StatusBadge } from '@ui/console/primitives';
import { MetricBand, overLoadedRows, trendFrom } from '@ui/console/MetricBand';
import { Term } from '@ui/console/glossary';
import { Quantity, formatCount, plural } from '@ui/console/measures';
import { RecordTable } from './record-table';
import { SelectField, TextField } from '@ui/console/forms';
import { configureAdminApi, currentRole, handleUnauthorized } from './auth';
import { canAccess } from './auth-policy';
import { Actions, DetailList, Page } from './components';

type DashboardState = {
  dashboard?: DashboardSummary;
  health?: HealthSummary;
  metrics?: MetricsSummary;
  releases?: AdminPilotControls['releases'];
};

/**
 * A release identifier short enough to read whole.
 *
 * A 40-character sha rendered into a tile came out as `2acdff8e4eef74f…` - cut
 * by the layout at whatever width was left rather than at a boundary that
 * means anything. Shortening it deliberately is the same information and it is
 * not truncated; the full value stays on hover and stays selectable.
 */
function shortRelease(release: string): string {
  return /^[0-9a-f]{12,}$/i.test(release) ? release.slice(0, 8) : release;
}

/**
 * The AI-spend figure reads `ai_runs`, which nothing in production writes. A
 * zero there is not a measured amount, so the tile says "not recorded" instead,
 * as AI costs does.
 */
export function aiSpendRecorded(metrics: MetricsSummary): boolean {
  return metrics.ai_calls_24h > 0 && Number(metrics.ai_cost_24h) > 0;
}

const HEALTH_FAILURE_LABELS: Record<string, string> = {
  tasks: 'background tasks dead-lettered',
  pipeline_stages: 'garment pipeline stages failed',
  ai: 'AI runs failed',
};

const HEALTH_QUEUE_LABELS: Record<string, string> = {
  tasks: 'background tasks queued',
  notifications: 'notifications queued',
};

const DEPENDENCIES: Array<[keyof Pick<HealthSummary, 'database' | 'redis' | 'storage'>, string]> = [
  ['database', 'Database'],
  ['redis', 'Cache and queue'],
  ['storage', 'File storage'],
];

/**
 * Why the health badge reads what it reads. The badge turns "degraded" on any
 * failure in the last 24 hours across the dependency checks and three failure
 * counts, some of which no tile on this page shows; without the reason beside
 * it, a "degraded" badge over a row of zeros reads as a false alarm.
 */
export function healthReasons(health: HealthSummary): {
  failing: string[];
  failures: string[];
  queues: string[];
} {
  const label = (labels: Record<string, string>, key: string) =>
    labels[key] ?? key.replaceAll('_', ' ');
  return {
    failing: DEPENDENCIES.filter(([key]) => health[key] !== 'ok').map(
      ([key, name]) => `${name} ${health[key]}`,
    ),
    failures: Object.entries(health.failures).map(
      ([key, value]) => `${formatCount(value)} ${label(HEALTH_FAILURE_LABELS, key)}`,
    ),
    queues: Object.entries(health.queues).map(
      ([key, value]) => `${formatCount(value)} ${label(HEALTH_QUEUE_LABELS, key)}`,
    ),
  };
}

function HealthReasons({ health }: { health: HealthSummary }) {
  const { failing, failures, queues } = healthReasons(health);
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {failing.length ? <span>{failing.join(', ')}.</span> : null}
      {failures.length ? <span>In the last 24 hours: {failures.join(', ')}.</span> : null}
      {queues.length ? <span>Waiting now: {queues.join(', ')}.</span> : null}
    </span>
  );
}

export function Dashboard() {
  const canUsePrivilegedOperatorTools = canAccess(currentRole(), 'admin:privileged-operator');
  const [state, setState] = useState<DashboardState>({});
  const [failed, setFailed] = useState<Set<keyof DashboardState>>(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date>();
  const load = useCallback(async () => {
    configureAdminApi();
    setLoading(true);
    const requests = await Promise.allSettled([
      dashboardApiV1InternalDashboardGet(),
      healthApiV1InternalHealthDashboardGet(),
      metricsApiV1InternalMetricsDashboardGet(),
      // Which build is actually serving. Nothing else in the console answers
      // it, and the screen that used to - Failed tasks & controls - had no
      // other reason to exist. The endpoint is privileged, so a role without
      // it simply does not ask rather than showing a failed read.
      ...(canUsePrivilegedOperatorTools ? [pilotControlsApiV1InternalPilotControlsGet()] : []),
    ]);
    const keys = ['dashboard', 'health', 'metrics', 'releases'] as const;
    const nextFailed = new Set<keyof DashboardState>();
    requests.forEach((request) => {
      if (request.status === 'fulfilled') {
        handleUnauthorized(request.value.response.status);
      }
    });
    setState((current) => {
      const next = { ...current };
      requests.forEach((request, index) => {
        const key = keys[index]!;
        if (request.status === 'fulfilled' && request.value.data && !request.value.error) {
          // The pilot-controls read carries four unrelated summaries; only the
          // release one has no other home, so only that is kept here.
          next[key] =
            key === 'releases'
              ? ((request.value.data as AdminPilotControls).releases as never)
              : (request.value.data as never);
        } else {
          nextFailed.add(key);
        }
      });
      return next;
    });
    setFailed(nextFailed);
    setRefreshedAt(new Date());
    setLoading(false);
  }, [canUsePrivilegedOperatorTools]);
  useEffect(() => void load(), [load]);

  const { dashboard, health, metrics, releases } = state;
  const reads = canUsePrivilegedOperatorTools ? 4 : 3;
  // Both task figures now come from `fastapi_worker_jobs` - dead-lettered and
  // queued - rather than from `task_executions`, which the runtime stopped
  // writing. They were rendered as "No source" long after the backend was
  // repointed, so the first screen an operator opens hid a five-figure
  // dead-letter count behind a placeholder.
  const deadLettered = dashboard?.failed_tasks ?? 0;
  const queuedTasks = metrics?.tasks_queued ?? 0;
  const pendingNotifications = dashboard?.pending_notifications ?? 0;
  const enabledAccounts = dashboard?.active_users ?? 0;
  const activeStylists = dashboard?.active_stylists ?? 0;
  return (
    <Page
      title="Operations overview"
      purpose="Use this page to answer, in one glance, whether the pilot is up, how much work is waiting, and what has failed in the last 24 hours. How much the product is being used, and whether what it produces is any good, is on Product metrics. Every figure below is a live read; nothing here changes anything."
      band={
        <MetricBand
          label="The last 24 hours"
          figures={[
            {
              id: 'ai-spend',
              label: 'AI spend',
              ...(!metrics
                ? { absent: 'The metrics read has not come back yet.' }
                : aiSpendRecorded(metrics)
                  ? {
                      value: String(metrics.ai_cost_24h),
                      raw: metrics.ai_cost_24h,
                      hint: `${formatCount(metrics.ai_calls_24h)} paid model calls. This is a money amount, but the overview endpoint does not report which currency — AI costs names it.`,
                    }
                  : {
                      // Not a zero: nothing records model calls or their cost
                      // where this figure is read from, so a 0 would be a
                      // claim the data cannot support. Same wording as AI costs.
                      value: 'not recorded',
                      tone: 'warning' as const,
                      statusLabel: 'nothing writes this',
                      hint: 'No model call with a cost was recorded in the last 24 hours, so there is no amount to show. AI costs explains why.',
                    }),
              ...(failed.has('metrics')
                ? { tone: 'warning' as const, statusLabel: 'could not refresh' }
                : {}),
            },
            {
              id: 'dead-lettered',
              label: (
                <>
                  Tasks <Term term="dead_lettered">dead-lettered</Term>
                </>
              ),
              ...(dashboard
                ? { value: formatCount(deadLettered), raw: dashboard.failed_tasks }
                : { absent: 'The operations read has not come back yet.' }),
              unit: plural(deadLettered, 'task'),
              hint: 'Jobs that used up every attempt and were set aside in the last 24 hours. They will not run again on their own — open Workflow jobs to read what each one died on.',
              tooltip:
                'Counted over fastapi_worker_jobs, the live queue, on dead_lettered_at — when the job was set aside, not when it was enqueued — over the same 24 hours as the figures beside it. Jobs dead-lettered earlier are not counted here even though they are still sitting in the queue.',
              ...(deadLettered > 0
                ? { tone: 'error' as const, statusLabel: 'needs attention' }
                : failed.has('dashboard')
                  ? { tone: 'warning' as const, statusLabel: 'could not refresh' }
                  : {}),
            },
          ]}
        />
      }
      actions={
        <>
          <Button size="md" color="link-gray" href="/product-metrics">
            Product metrics
          </Button>
          <Button size="md" color="secondary" isDisabled={loading} onClick={() => void load()}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-tertiary">
        <StatusBadge value={health?.status ?? (loading ? 'checking' : 'unavailable')} />
        {health ? <HealthReasons health={health} /> : null}
        <span>
          {refreshedAt
            ? `Updated ${refreshedAt.toLocaleTimeString()}`
            : 'Connecting to operations data'}
        </span>
        {failed.size ? (
          <span className="flex items-center gap-2">
            <StatusBadge value={`${failed.size} of ${reads} reads failed`} />
            <span>
              Those tiles still show the last figures that arrived, so they are older than the rest
              of the page.
            </span>
          </span>
        ) : null}
      </div>

      <section className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <Section
          title="What is running, and what it runs on"
          description="The three systems the product cannot run without, and the build currently serving. Anything other than a healthy reading here explains failures everywhere else."
        >
          <DetailList
            items={[
              ['Database', <StatusBadge value={health?.database ?? 'unavailable'} />],
              ['Cache and queue (Valkey)', <StatusBadge value={health?.redis ?? 'unavailable'} />],
              ['Image and file storage', <StatusBadge value={health?.storage ?? 'unavailable'} />],
              // The console's only answer to "what is actually deployed". It
              // used to sit on Failed tasks & controls, where nothing else on
              // the screen had anything to do with it.
              ...(canUsePrivilegedOperatorTools
                ? ([
                    [
                      'Backend release now serving',
                      releases ? (
                        <Quantity
                          value={shortRelease(releases.backend_release)}
                          raw={releases.backend_release}
                        />
                      ) : (
                        'not read'
                      ),
                    ],
                    ['Environment', releases ? releases.environment : 'not read'],
                  ] satisfies Array<[ReactNode, ReactNode]>)
                : []),
            ]}
          />
        </Section>
        <Section
          title="Work waiting to be done"
          description="How much is queued up right now. These are counts of things, not rates."
        >
          <DetailList
            items={[
              [
                'Background tasks queued',
                <Quantity
                  value={formatCount(queuedTasks)}
                  unit={plural(queuedTasks, 'task')}
                  raw={metrics?.tasks_queued}
                />,
              ],
              [
                'Notifications not yet sent',
                <Quantity
                  value={formatCount(pendingNotifications)}
                  unit={plural(pendingNotifications, 'notification')}
                  raw={dashboard?.pending_notifications}
                />,
              ],
            ]}
          />
          <Actions>
            <Button size="sm" color="link-gray" href="/workflow-jobs">
              Open the job queue
            </Button>
          </Actions>
        </Section>
      </section>
      {/* Standing counts rather than 24-hour ones, so they sit under the band
          rather than in it. Both were tiles on a band that had six. */}
      <Section
        title="Who is on the pilot"
        description="How many accounts exist and how many stylists can take work. How many of those members actually signed in is a different count, on Product metrics."
      >
        <DetailList
          items={[
            [
              'Enabled member accounts',
              <Quantity
                value={formatCount(enabledAccounts)}
                unit={plural(enabledAccounts, 'account')}
                raw={dashboard?.active_users}
              />,
            ],
            [
              'Stylists able to take new members',
              <Quantity
                value={formatCount(activeStylists)}
                unit={plural(activeStylists, 'stylist')}
                raw={dashboard?.active_stylists}
              />,
            ],
          ]}
        />
      </Section>
    </Page>
  );
}
/**
 * The emitters production is known to log under. The API filter is free text,
 * so this is a starting list, not a limit: any other value seen in the loaded
 * rows is offered too.
 */
export const KNOWN_APPLICATIONS = [
  'backend',
  'worker',
  'api',
  'admin',
  'deployment',
  'consumer',
  'stylist',
] as const;

export function applicationOptions(loaded: readonly string[], selected: string): string[] {
  const seen = new Set<string>(KNOWN_APPLICATIONS);
  for (const value of loaded) if (value) seen.add(value);
  if (selected !== 'all') seen.add(selected);
  return [...seen].sort();
}

export function ActivityLogs() {
  const [application, setApplication] = useState('all');
  const [eventType, setEventType] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [items, setItems] = useState<ActivityLogItem[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [pages, setPages] = useState(0);
  const pagesRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const load = useCallback(
    async (append = false, pageCursor?: string) => {
      configureAdminApi();
      setLoading(true);
      setError(undefined);
      const result = await activityLogsApiV1InternalActivityLogsGet({
        query: {
          limit: 50,
          application: application === 'all' ? null : application,
          event_type: eventType.trim() || null,
          correlation_id: correlationId.trim() || null,
          cursor: append ? (pageCursor ?? null) : null,
        },
      });
      setLoading(false);
      handleUnauthorized(result.response.status);
      if (result.error || !result.data) {
        setError('The event log could not be refreshed. Existing results are still available.');
        return;
      }
      setItems((current) => (append ? [...current, ...result.data.items] : result.data.items));
      setCursor(result.data.next_cursor ?? undefined);
      pagesRef.current = append ? pagesRef.current + 1 : 1;
      setPages(pagesRef.current);
    },
    [application, correlationId, eventType],
  );
  useEffect(() => {
    void load();
    // Refreshing replaces the list with the newest page, which would throw
    // away older pages an operator has loaded with Load more. So the timer
    // stands down while more than one page is on screen.
    const interval = window.setInterval(() => {
      if (pagesRef.current <= 1) void load();
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [load]);
  const consumerEvents = items.filter((item) => item.application === 'consumer').length;
  const eventTypes = new Set(items.map((item) => item.event_type)).size;
  const applications = applicationOptions(
    items.map((item) => item.application),
    application,
  );
  return (
    <Page
      title="Event log"
      purpose="Use this page to follow, in order, the events every part of the system records: the backend and its workers, the consumer and stylist apps, admin actions and deployments. It refreshes itself every 15 seconds while you are on the newest page. What happened is recorded; what anyone typed is not."
      band={
        <MetricBand
          label="What has been recorded under these filters"
          figures={[
            {
              id: 'loaded',
              label: 'Events loaded',
              value: formatCount(items.length),
              unit: plural(items.length, 'event'),
              raw: items.length,
              hint: (
                <>
                  This feed pages by <Term term="cursor" />, which cannot report a total. Load more
                  to read further back.
                </>
              ),
            },
            {
              id: 'consumer',
              label: 'From the consumer app',
              value: formatCount(consumerEvents),
              unit: plural(consumerEvents, 'event'),
              raw: consumerEvents,
              bar: {
                fraction: items.length ? consumerEvents / items.length : 0,
                label: `From the consumer app: ${consumerEvents} of ${items.length} loaded`,
              },
              hint: 'The rest came from other parts of the system: the backend, its workers, the stylist app, admin or deployments. Narrow with the Application filter to read one on its own.',
              tooltip: overLoadedRows(items.length),
            },
            {
              id: 'kinds',
              label: 'Distinct event types',
              value: formatCount(eventTypes),
              unit: plural(eventTypes, 'type'),
              raw: eventTypes,
              hint: 'How many different things happened, rather than how often. One type repeating is a different picture from many types once.',
              tooltip: overLoadedRows(items.length),
            },
          ]}
          trend={{
            points: trendFrom(items.map((item) => new Date(item.occurred_at).getTime())),
            noun: 'events',
            caption: 'When the loaded events happened. A period with nothing in it keeps its slot.',
          }}
        />
      }
      toolbar={{
        onRefresh: () => void load(),
        filters: (
          <form
            className="flex flex-1 flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <SelectField
              label="Application"
              hint="The part of the system that recorded the event."
              value={application}
              onChange={(event) => setApplication(event.target.value)}
              options={[
                { value: 'all', label: 'All' },
                ...applications.map((value) => ({ value, label: value })),
              ]}
            />
            <TextField
              label="Event type"
              hint="The name of the thing that happened, exactly as it was recorded."
              value={eventType}
              onChange={(event) => setEventType(event.target.value)}
            />
            <TextField
              label="Correlation ID"
              hint="Paste one to see every event from the same request or flow together."
              value={correlationId}
              onChange={(event) => setCorrelationId(event.target.value)}
            />
            <Button size="md" color="secondary" type="submit">
              Apply filters
            </Button>
          </form>
        ),
      }}
    >
      {error ? <Callout tone="error" title={error} /> : null}
      {pages > 1 ? (
        <Callout tone="neutral" title="Auto-refresh is paused while older pages are loaded.">
          Refreshing would replace them with the newest page. Press Refresh to go back to the newest
          events and resume.
        </Callout>
      ) : null}
      <RecordTable
        noun="events"
        records={items}
        columns={[
          'occurred_at',
          'application',
          'actor_kind',
          'event_type',
          'aggregate_type',
          'correlation_id',
          'request_id',
        ]}
        emptyHint="Nothing has been recorded under these filters. The read succeeded and came back empty — this is an answer, not a failure. Set the application back to All and clear the two text filters to see everything."
      />
      {cursor ? (
        <Actions>
          <Button
            size="md"
            color="secondary"
            isDisabled={loading}
            onClick={() => void load(true, cursor)}
          >
            Load more
          </Button>
        </Actions>
      ) : null}
    </Page>
  );
}

export function PushDelivery() {
  const [summary, setSummary] = useState<AdminPilotControls['push']>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const load = useCallback(async () => {
    configureAdminApi();
    setLoading(true);
    const result = await pilotControlsApiV1InternalPilotControlsGet();
    setLoading(false);
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setError(
        'Push delivery status could not be refreshed. The previous snapshot remains visible.',
      );
      return;
    }
    setError(undefined);
    setSummary(result.data.push);
  }, []);
  useEffect(() => void load(), [load]);
  // Queued and failed are the two ways a notification has not reached a phone,
  // so each is drawn against their sum rather than against nothing.
  const attempted = (summary?.queued ?? 0) + (summary?.failed ?? 0);
  const pushAbsent = error
    ? 'The push read failed. Press Refresh to try it again.'
    : 'The push read has not come back yet.';
  return (
    <Page
      title="Push delivery"
      purpose="Use this page to check whether push notifications are reaching members' phones: how many devices are registered, how many messages are waiting, and how many could not be delivered."
      band={
        <MetricBand
          label="Whether push is getting through"
          figures={[
            {
              id: 'devices',
              label: 'Devices registered',
              ...(summary
                ? {
                    value: formatCount(summary.active_tokens),
                    unit: plural(summary.active_tokens, 'device'),
                    raw: summary.active_tokens,
                  }
                : { absent: pushAbsent }),
              hint: 'Phones that have accepted notifications and can be reached. Nothing can be delivered to a member with none.',
            },
            {
              id: 'queued',
              label: 'Waiting to be sent',
              ...(summary
                ? {
                    value: formatCount(summary.queued),
                    unit: plural(summary.queued, 'notification'),
                    raw: summary.queued,
                    ...(summary.queued > 0
                      ? { tone: 'warning' as const, statusLabel: 'queue not empty' }
                      : {}),
                    ...(attempted > 0
                      ? {
                          bar: {
                            fraction: summary.queued / attempted,
                            label: `Waiting to be sent: ${summary.queued} of ${attempted} notifications not yet delivered`,
                            tone: 'warning' as const,
                          },
                        }
                      : {}),
                  }
                : { absent: pushAbsent }),
              hint: 'Accepted and queued. A small number here is normal; a number that keeps climbing is not.',
            },
            {
              id: 'failed',
              label: 'Could not be delivered, all time',
              ...(summary
                ? {
                    value: formatCount(summary.failed),
                    unit: plural(summary.failed, 'notification'),
                    raw: summary.failed,
                    ...(summary.failed > 0
                      ? { tone: 'error' as const, statusLabel: 'needs attention' }
                      : {}),
                    ...(attempted > 0
                      ? {
                          bar: {
                            fraction: summary.failed / attempted,
                            label: `Could not be delivered: ${summary.failed} of ${attempted} notifications not yet delivered`,
                            tone: 'error' as const,
                          },
                        }
                      : {}),
                  }
                : { absent: pushAbsent }),
              hint: 'Every notification ever sent that the provider rejected or whose device was unreachable — not a recent window.',
            },
            {
              id: 'receipt-pending',
              label: 'Waiting on a delivery receipt',
              ...(summary
                ? {
                    value: formatCount(summary.receipt_pending),
                    unit: plural(summary.receipt_pending, 'notification'),
                    raw: summary.receipt_pending,
                  }
                : { absent: pushAbsent }),
              hint: 'Handed to the provider; we have not yet heard whether it reached the phone.',
            },
          ]}
        />
      }
      actions={
        <Button size="md" color="secondary" isDisabled={loading} onClick={() => void load()}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {error ? <Callout tone="error" title={error} /> : null}
      <Section
        title="What this page cannot do"
        description="The limits of this screen, so you do not go looking for a control that is not here."
      >
        <Callout tone="info" title="You can read delivery state here, but not send anything">
          This page reports what the queue and the provider's receipts say. The keys we use to talk
          to Apple, Google and Expo are held by the server and are never shown here, and there is no
          test-send button on this screen.
        </Callout>
        {summary ? (
          <DetailList
            items={[
              [
                'Not sent because push was off, all time',
                <Quantity
                  value={formatCount(summary.disabled)}
                  unit={plural(summary.disabled, 'notification')}
                  raw={summary.disabled}
                />,
              ],
              ['Why there is no test send', summary.gate_reason],
              [
                'Live external side effects',
                <span>
                  {summary.live_external_side_effects ? 'on' : 'off'} — for information only. Push
                  delivery does not depend on this setting.
                </span>,
              ],
            ]}
          />
        ) : null}
        <Actions>
          <Button size="sm" color="link-gray" href="/activity-logs">
            Review notification activity
          </Button>
        </Actions>
      </Section>
    </Page>
  );
}

export function NotFound() {
  return (
    <Page
      title="Page not found"
      purpose="There is no Admin screen at this address. Nothing is wrong with your account or your permissions — the link is simply not one of ours."
    >
      <Actions>
        <Button size="md" color="primary" href="/">
          Return to operations
        </Button>
      </Actions>
    </Page>
  );
}
